import { type VariantProps, cva } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Briefing Deck button — a stamp on a form, not a pill.
 *
 * Solid actions are printed ink on paper. The marker is NEVER a button fill:
 * it is the console's "look at this", and a page whose every action shouts has
 * no way left to point.
 *
 * `destructive` is the one place a status colour fills a control, and it earns
 * it — revoking a license is real product state changing, and the modal that
 * gates it demands a typed reason anyway.
 *
 * Focus deliberately carries no ring: globals.css declares a 2px solid ink
 * outline (marker at night) on `:focus-visible` in @layer base, and NOT
 * setting `outline-none` here is what lets every button inherit it.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[2px] font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-ink text-paper hover:bg-ink-2",
        destructive: "bg-hold text-paper hover:bg-hold/90",
        outline: "border border-rule-strong bg-transparent text-ink hover:border-ink hover:bg-sunk",
        secondary: "bg-sunk text-ink hover:bg-rule",
        ghost: "text-ink-2 hover:bg-sunk hover:text-ink",
        link: "h-auto px-0 text-ink-2 underline-offset-4 hover:text-ink hover:underline",
      },
      size: {
        default: "h-9 px-3.5 text-[0.875rem] has-[>svg]:px-3",
        xs: "h-6 gap-1 px-2 text-[0.75rem] [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 text-[0.8125rem] has-[>svg]:px-2.5",
        lg: "h-10 px-5 text-[0.9375rem] has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  type,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      // A <button> inside a <form> with no type submits it. Half this console
      // is search forms with inline row actions, so the default is explicit.
      // `asChild` renders an <a> or a <Link>, which must not carry one.
      {...(asChild ? {} : { type: type ?? "button" })}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
